"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { formatDistanceToNow } from "date-fns";
import { MapPin, Trash2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ItineraryCreationForm from "@/components/ItineraryCreationForm";
import { getMyTrips, removeMyTrip, type MyTripEntry } from "@/app/lib/myTrips";

function tripHref(trip: MyTripEntry) {
  const citySlug = encodeURIComponent(trip.city.toLowerCase().trim());
  return trip.itinid === "DEFAULT"
    ? `/show/${citySlug}`
    : `/show/${citySlug}/${trip.itinid}`;
}

export default function MyTripsPage() {
  const [trips, setTrips] = useState<MyTripEntry[] | null>(null);

  useEffect(() => {
    setTrips(getMyTrips());
  }, []);

  const handleRemove = (trip: MyTripEntry) => {
    removeMyTrip(trip.city, trip.itinid);
    setTrips(getMyTrips());
  };

  return (
    <div className="w-full min-h-screen bg-white text-gray-800 flex flex-col">
      <Header />

      <section className="relative w-full h-56 md:h-64 flex flex-col justify-center items-center bg-gray-900">
        <div className="relative z-10 text-white text-center px-4">
          <h1 className="text-3xl md:text-5xl font-bold mb-3">My Trips</h1>
          <p className="text-base md:text-lg max-w-xl mx-auto font-light text-white/80">
            Itineraries you&apos;ve created or viewed on this device
          </p>
        </div>
      </section>

      <main className="flex-grow container mx-auto px-4 py-16 max-w-5xl">
        {trips === null ? null : trips.length === 0 ? (
          <div className="text-center max-w-xl mx-auto">
            <MapPin
              className="w-12 h-12 mx-auto text-orange-400 mb-4"
              aria-hidden="true"
            />
            <h2 className="text-2xl font-semibold mb-3">
              You haven&apos;t planned any trips yet
            </h2>
            <p className="text-gray-600 mb-8">
              Trips you create or view are saved here automatically on this
              device — no account needed. Start by telling us where you want
              to go.
            </p>
            <div className="bg-gray-900 rounded-2xl p-8">
              <ItineraryCreationForm />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {trips.map((trip) => (
              <div
                key={`${trip.city}:${trip.itinid}`}
                className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow"
              >
                <h3 className="text-xl font-semibold mb-1">{trip.cityName}</h3>
                <p className="text-sm text-gray-500 mb-5">
                  Viewed{" "}
                  {formatDistanceToNow(new Date(trip.visitedAt), {
                    addSuffix: true,
                  })}
                </p>
                <div className="flex items-center gap-3">
                  <Link
                    href={tripHref(trip)}
                    className="flex-grow text-center rounded-full bg-gradient-to-r from-orange-500 to-amber-400 text-white font-semibold py-2 px-4 hover:from-orange-600 hover:to-amber-500 transition-colors"
                  >
                    View Itinerary
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleRemove(trip)}
                    aria-label={`Remove ${trip.cityName} from My Trips`}
                    className="p-2 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <Trash2 className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
